import { Me } from "@rhybus/api"
import { ACTIVITY_ID, isProcessorDisabled } from "../../../Configuration"
import { customerRoles, ListNames, TRMRoles } from "../../../shared/constants/globalConstants"
import { anewRListsHttp } from "../../../shared/services/api/anew-r-lists-http"
import { anewWhoHttp } from "../../../shared/services/api/anew-who-http"
import { getAllUsersFromGroupName } from "../../../shared/services/api/users"
import { generateGUID } from "../../../shared/utils/globalFunctions"
import { I_InquiryList, TRMNotificationMetadata } from "../../../types/listDataTypes"
import { anewNotifyHttp } from "../../../shared/services/api/anew-notify-http"
import { convertDate } from "../../../global"
import { getItemById } from "./trmInquiryList"
import { ensureDeferredList } from "./trmMetadataService"
const USER_DISABLED_EMAILS = false
export const postItemToNotificationList = async(notificationItem : any) =>{
    const historyList = await anewRListsHttp().getList(ListNames?.TRMNotificationsList)
    const historyListItem=historyList.addOrUpdateItem({...notificationItem})
    return historyListItem 
}
export const getNotificationItems = async(userId: string)=> {
    const list = await anewRListsHttp().getList(ListNames?.TRMNotificationsList)
    const filter = `ToUser eq '${userId}' and IsRead eq false`
    const items = await list
        .query()
        .filter(filter)
        .get()
    return items
}
const escapeHTML = (str: any) =>
    String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
export const triggerTRMNotifications = async (
    requirement: I_InquiryList | null | any,
    notificationMetadata: TRMNotificationMetadata[],
    activityID: string,
    currentUser: Me | null,
    extraData?: any,
    cancelEmail?:any,
    vehicleInformation?:any
): Promise<void> => {
    let latestRequirement = requirement
    if (requirement?.InquiryGUID) {
        try {
            const latest = await getItemById(requirement.InquiryGUID)
            latestRequirement = {
                ...latest,
                ...requirement,
                Territory: latest?.Territory ?? requirement?.Territory,
                State: latest?.State ?? requirement?.State,
                TeamMembers: latest?.TeamMembers ?? requirement?.TeamMembers,
                InquiryeKey: latest?.InquiryeKey ?? requirement?.InquiryeKey,
                InquiryeTag: latest?.InquiryeTag ?? requirement?.InquiryeTag,
            }
        } catch (error) {
            console.error('Error refreshing notification requirement:', error)
        }
    }
    const specificUserNotifications = [ACTIVITY_ID.AddedTeamMemberInquiry, ACTIVITY_ID.RemovedTeamMemberWorkOrder,ACTIVITY_ID?.AddedTeamMemberWorkOrder,ACTIVITY_ID?.RemovedTeamMemberInquiry,ACTIVITY_ID.InboundActionRequired,ACTIVITY_ID.SalesActionRequired,ACTIVITY_ID?.InquiryUpdated,ACTIVITY_ID?.WorkOrderUpdated,ACTIVITY_ID?.SalesRepondedInquiry,ACTIVITY_ID?.SalesRepondedWorkOrder,ACTIVITY_ID?.InboundResponded,ACTIVITY_ID?.SalesActionRequiredworkOrder,ACTIVITY_ID?.OutboundResponded,ACTIVITY_ID?.InquiryCancelDenied,ACTIVITY_ID?.WorkOrderCancelDenied,ACTIVITY_ID?.InquiryCancelRequest,ACTIVITY_ID?.WorkOrderCancelRequest,ACTIVITY_ID?.WorkOrderReinitiated]
    // Notification metadata is hydrated in the background after first paint. An action
    // fired inside that window used to filter [] down to nothing and return "success"
    // having sent no email and raised no bell notification, so wait for it here.
    const metadata = await ensureDeferredList<TRMNotificationMetadata>(ListNames.TRMNotificationsMetadataList, notificationMetadata)
    const emailNotifications = metadata?.filter(notification => notification?.AlertType === 'EMAIL')
    const bellNotifications = specificUserNotifications?.includes(activityID)
        ? metadata?.filter(notification => notification?.AlertType === 'NOTIFICATION' || notification?.AlertType === 'ACTION')
        : metadata?.filter(notification => (notification?.AlertType === 'NOTIFICATION' || notification?.AlertType === 'ACTION') && notification?.StatusCode === requirement?.StatusCode)
    const removeDuplicatesInNotifications = specificUserNotifications?.includes(activityID) ? bellNotifications : Array.from(new Set(bellNotifications?.map((item) => JSON.stringify(item))))?.map((item) => JSON.parse(item))
    await sendEmailNotifications(latestRequirement, emailNotifications, activityID, currentUser, extraData, cancelEmail, vehicleInformation)
    await pushNotificationsToDashboard(latestRequirement, removeDuplicatesInNotifications, activityID, currentUser, extraData)
}
const pushNotificationsToDashboard = async (
    requirement: I_InquiryList | null,
    bellNotifications: TRMNotificationMetadata[],
    activityID: string,
    userContext: Me | null,
    extraData?: any
) => {
    if (!requirement) return
    let filteredNotifications = bellNotifications?.filter(notification => notification?.ActivityID === activityID)
    const specificUserNotifications = [ACTIVITY_ID.AddedTeamMemberInquiry, ACTIVITY_ID.RemovedTeamMemberWorkOrder,ACTIVITY_ID?.AddedTeamMemberWorkOrder,ACTIVITY_ID?.RemovedTeamMemberInquiry]
    for (const notification of filteredNotifications) {
        if (notification?.Role === 'Submitter') {
            await sendNotificationToSubmitter(requirement, notification, userContext)
        } else if (specificUserNotifications?.includes(notification.ActivityID)) {
            await sendNotificationToSpecificUser(requirement, notification, userContext, extraData)
        }
        else {
            await sendNotificationsToRoles(requirement, notification, userContext)
        }
    }
}
const sendNotificationToSubmitter = async (
    requirement: I_InquiryList,
    notification: TRMNotificationMetadata,
    userContext: Me | null,
) => {
    const notificationObject = await createNotificationObject(requirement, notification, userContext, requirement?.ItemCreatedBy?.userID, 'Submitter')
    await saveNewNotifications(notificationObject, userContext)
}
const sendNotificationToSpecificUser = async (
    requirement: I_InquiryList,
    notification: any,
    userContext: Me | null,
    extraData?: any
) => {
    const notificationRecipient: string[] = []
    if (extraData?.teamMemberAddedOrRemoved && typeof (extraData?.teamMemberAddedOrRemoved) !== 'string') {
        extraData?.teamMemberAddedOrRemoved?.forEach((person: any) => {
            notificationRecipient.push(person?.rid ? person?.rid : person?.userId)
        })
    }
    if(extraData?.salesTeamMember && typeof (extraData?.salesTeamMember) !== 'string') {
        extraData?.salesTeamMember?.forEach((person: any) => {
            notificationRecipient.push(person?.rid ? person?.rid : person?.userId)
        })
    }
    if(extraData?.processor && typeof (extraData?.processor) !== 'string') {
        extraData?.processor?.forEach((person: any) => {
            notificationRecipient.push(person?.rid ? person?.rid : person?.userId)
        })
    }

    const notificationRecepients = notificationRecipient?.length === 0 ? userContext?.userId : notificationRecipient[0]
    if(notificationRecipient !== undefined){
        const notificationObject = await createNotificationObject(requirement, notification, userContext, notificationRecepients, undefined, extraData)
        await saveNewNotifications(notificationObject, userContext)
    }
}
const sendNotificationsToRoles = async (
    requirement: I_InquiryList|any,
    notification: any,
    userContext: Me | null
) => {
    const userRoles = notification?.ToUserRoles
    let filteredRoles = userRoles
    if (typeof userRoles === 'string') {
        if (userRoles.includes(',')) {
            filteredRoles = userRoles?.split(',')?.map(role => findRolesFromNotifications(requirement, role))
        } else {
            filteredRoles = [findRolesFromNotifications(requirement, notification?.ToUserRoles)]
        }
    }
    filteredRoles = isProcessorDisabled ? filteredRoles.filter((role : string) => role !== TRMRoles?.processor):filteredRoles
    const filteredSalesAndProcessorRoles = filteredRoles?.filter((role : string) => role === TRMRoles?.sales || role === TRMRoles?.processor)
    if(filteredSalesAndProcessorRoles?.length > 0){
        for (const group of filteredSalesAndProcessorRoles) {
            if(group === TRMRoles?.sales && requirement?.AnewAccOwnerID){
                const AnewAccOwnerID = typeof (requirement?.AnewAccOwnerID) === 'string' ? requirement?.AnewAccOwnerID : requirement?.AnewAccOwnerID?.userID
                await sendNotificationToSpecificUser(requirement, notification, userContext, {salesTeamMember: [{rid: AnewAccOwnerID}]})
            }else if(group === TRMRoles?.processor && requirement?.AssignedProcessor){
                const processorId = typeof (requirement?.AssignedProcessor) === 'string' ? requirement?.AssignedProcessor : requirement?.AssignedProcessor?.userID
                await sendNotificationToSpecificUser(requirement, notification, userContext, {processor: [{rid: processorId}]})
            }
        }
    }
    filteredRoles = filteredRoles?.filter((role : string) => role !== TRMRoles?.sales && role !== TRMRoles?.processor)
    if (filteredRoles?.length > 0) {
        const notificationPromises: Promise<void>[] = []
        const notifiedUsers = new Set<any>()

        for (const group of filteredRoles) {
            const groupUsers = await getAllUsersFromGroupName(group) as any[]
            for (const person of groupUsers) {
                if (!notifiedUsers.has(person?.rid)) {
                    const notificationObject = await createNotificationObject(requirement, notification, userContext, person?.rid, group)
                    const notificationPromise = saveNewNotifications(notificationObject, userContext) as Promise<void>
                    notificationPromises.push(notificationPromise)
                    notifiedUsers.add(person.rid)
                }
            }
        }

        await Promise.all(notificationPromises)
    }
}
const createNotificationObject = async (
    requirement: I_InquiryList | any,
    notification: TRMNotificationMetadata,
    userContext: Me | null,
    toUser?: string,
    toUserRole?: string,
    extraData?: any
): Promise<any> => {

    let subjectWithReplacedValues = notification?.Subject
    subjectWithReplacedValues = subjectWithReplacedValues?.replace(
        /\[ID#\]/g,
        String(requirement?.ID).padStart(6, '0') ?? ''
    )

    if (subjectWithReplacedValues?.includes('[SubmitterName]')) {
        const submitter = await anewWhoHttp().OtherByUserId(requirement?.ItemCreatedBy as string)
        if (submitter?.persona) {
            subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                /\[SubmitterName\]/g,
                submitter?.persona
            )
        }
    }

    if (extraData?.fileName && subjectWithReplacedValues?.includes('[DocName]')) {
        subjectWithReplacedValues = subjectWithReplacedValues?.replace(
            /\[DocName\]/g,
            extraData?.fileName
        )
    }

    if (subjectWithReplacedValues?.includes('[UserName]')) {
        if (userContext) {
            const completedBy = await anewWhoHttp().OtherByUserId(userContext.userId)
            if (completedBy?.persona) {
                subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                    /\[UserName\]/g,
                    completedBy?.persona
                )
            }
        }
    }
    if (subjectWithReplacedValues?.includes('[ProcessorName]')) {
        if (requirement?.AssignedProcessor) {
            const processorId = typeof (requirement?.AssignedProcessor) === 'string' ? requirement?.AssignedProcessor : requirement?.AssignedProcessor?.userID
            const processorName = await anewWhoHttp().OtherByUserId(processorId)
            if (processorName?.persona) {
                subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                    /\[ProcessorName\]/g,
                    processorName?.persona
                )
            }
        }
    }

    const notificationSubject = subjectWithReplacedValues
    let toUserDefined; 
    if(toUser){
        toUserDefined = toUser
    }
    else{
        toUserDefined = toUser || requirement?.ItemCreatedBy?.userID
    }
    return {
        NotificationGUID: generateGUID(),
        InquiryGUID: requirement?.InquiryGUID,
        ToUser: toUserDefined as string,
        ToUserRole: toUserRole || '',
        Subject: notificationSubject,
        NotificationType: (notification as any)?.AlertType,
        InquiryID: String(requirement?.ID).padStart(6, '0') ?? '',
        IsRead: false,
        StatusCode: requirement?.StatusCode,
        ItemCreated: new Date(),
        ItemCreatedBy: userContext?.userId,
        ItemModified: new Date(),
        ItemModifiedBy: userContext?.userId
    }
}
const findRolesFromNotifications = (requirement: I_InquiryList, role: string): string => {
    if(role === TRMRoles?.inbound){
        return `TRM ${requirement?.Territory} Inbound`
    }else if(role === TRMRoles?.outbound){
        return `TRM ${requirement?.Territory} Outbound`
    }else{
        return role
    }
}
export const saveNewNotifications = async (
  notification: TRMNotificationMetadata | Array<TRMNotificationMetadata>,
  userContext: any
): Promise<void> => {
  if (!userContext) {
    return;
  }

  try {
    if (Array.isArray(notification)) {
      await Promise.all(
        notification?.map((notif) => postItemToNotificationList(notif))
      );
    } else {
      await postItemToNotificationList(notification);
    }
  } catch (error) {
    console.error('Error saving notifications', error);
  }
}
const sendEmailNotifications = async (
    requirement: I_InquiryList | null,
    notificationMetadata: any[],
    activityID: string,
    currentUser: Me | null,
    extraData?: any,
    cancelEmail?:any,
    vehicleInformation?:any
) => {
    if (!requirement) {
        return // Early return if no requirement is present
    }

    try {
        const statusChangeEmails = [ACTIVITY_ID.AddedTeamMemberInquiry, ACTIVITY_ID.RemovedTeamMemberWorkOrder,ACTIVITY_ID?.AddedTeamMemberWorkOrder,ACTIVITY_ID?.RemovedTeamMemberInquiry,ACTIVITY_ID.InboundActionRequired,ACTIVITY_ID.SalesActionRequired,ACTIVITY_ID?.SalesRepondedInquiry,ACTIVITY_ID?.SalesRepondedWorkOrder,ACTIVITY_ID?.InboundResponded,ACTIVITY_ID?.SalesActionRequiredworkOrder,ACTIVITY_ID?.OutboundResponded,ACTIVITY_ID?.InquiryCancelDenied,ACTIVITY_ID?.WorkOrderCancelDenied,ACTIVITY_ID?.InquiryCancelRequest,ACTIVITY_ID?.WorkOrderCancelRequest,ACTIVITY_ID?.WorkOrderReinitiated,ACTIVITY_ID?.UserManagementUserOnboarded,ACTIVITY_ID?.UserManagementUserActivated,ACTIVITY_ID?.UserManagementUserDeactivated,ACTIVITY_ID?.UserManagementUserDeleted]
        const matchingNotifications = statusChangeEmails?.includes(activityID)
            ? notificationMetadata : notificationMetadata?.filter(notification => notification?.StatusCode === requirement?.StatusCode)
        if (matchingNotifications.length > 0) {

            const emailTriggeredBySubmitter =
                currentUser?.userId === requirement?.ItemCreatedBy?.userID;

            const filteredNotifications = matchingNotifications?.filter(notification => notification?.ActivityID === activityID)
            // .filter((notification) => {
            //     if (sendAllEmails.includes(activityID)) {
            //         return true // Include all notifications for sendAllEmails activities
            //     } else if (!emailTriggeredBySubmitter && notification.Role === 'Submitter') {
            //         return !emailTriggeredBySubmitter && notification.Role === 'Submitter'
            //     } else if (!emailTriggeredBySubmitter) {
            //         return notification.Role !== 'Submitter'
            //     } else {
            //         // Include only notifications triggered by the submitter
            //         return emailTriggeredBySubmitter && notification.Role === 'Submitter'
            //     }
            // })
            for (const notification of filteredNotifications) {
                const sendTo = notification?.IsEmailRequired ? await sendEmailTo(
                    requirement,
                    notification,
                    activityID,
                    emailTriggeredBySubmitter,
                    extraData,
                    cancelEmail
                ) : []
                if (Array.isArray(sendTo) && sendTo?.length > 0) {
                    const ccTo = await ccEmailTo(
                        requirement,
                        notification,
                        sendTo,
                        activityID
                    )
                    const subject = await buildEmailSubject(
                        requirement,
                        notification,
                        extraData,
                        currentUser
                    )
                    const body = await buildEmailBody(requirement, notification,currentUser, extraData, activityID, vehicleInformation)
                    const emailProps = {
                        To: sendTo,
                        CC: ccTo,
                        Subject: subject,
                        Body: body,
                    }
                    sendEmail(emailProps, requirement, notification, currentUser)
                }
            }
        }
    } catch (error) {
        console.error('Error sending email:', error)
    }
}
const sendEmailTo = async (
    requirement: I_InquiryList | any,
    notification: any,
    activityID: string,
    emailTriggeredBySubmitter: boolean,
    extraData?: any,
    cancelEmail?:any
    
): Promise<string[]> => {
    const emailRecipients: string[] = []
    const specificUserNotifications = [ACTIVITY_ID.AddedTeamMemberInquiry, ACTIVITY_ID.RemovedTeamMemberWorkOrder,ACTIVITY_ID?.AddedTeamMemberWorkOrder,ACTIVITY_ID?.RemovedTeamMemberInquiry,ACTIVITY_ID?.UserManagementUserOnboarded,,ACTIVITY_ID?.UserManagementUserActivated,ACTIVITY_ID?.UserManagementUserDeactivated,ACTIVITY_ID?.UserManagementUserDeleted]
    // Temporary hard coded value for sending all emails
    // This will change with User Prefs later in settings
    const isEmailEnabled = () => !USER_DISABLED_EMAILS

    const userRoles = notification?.ToUserRoles
    let filteredRoles = userRoles

    if (typeof userRoles === 'string') {
        if (userRoles?.includes(',')) {
            filteredRoles = userRoles?.split(',')?.map(role => findRolesFromNotifications(requirement,role))
        }else{
            if(userRoles === customerRoles[0]){
                filteredRoles = ''
            }else{
                filteredRoles = findRolesFromNotifications(requirement,notification?.ToUserRoles)
            }
        }
    }

    const addRecipientsFromUser = async (userId: string) => {
        const user = await anewWhoHttp().OtherByUserId(userId)
        if (user?.rid && isEmailEnabled()) {
            emailRecipients.push(user.rid)
        }
    }
    const addRecipientsFromGroup = async () => {
        if (Array.isArray(filteredRoles)) {
            const filteredSalesAndProcessorRoles = filteredRoles?.filter(role => role === TRMRoles?.sales || role === TRMRoles?.processor)
            if(filteredSalesAndProcessorRoles?.length > 0){
                if(filteredSalesAndProcessorRoles?.includes(TRMRoles?.sales) && requirement?.AnewAccOwnerID){
                    typeof (requirement?.AnewAccOwnerID) === 'string' ? emailRecipients.push(requirement?.AnewAccOwnerID) : emailRecipients.push(requirement?.AnewAccOwnerID?.userID)
                }else if(filteredSalesAndProcessorRoles?.includes(TRMRoles?.processor) && requirement?.AssignedProcessor){
                    typeof (requirement?.AssignedProcessor) === 'string' ? emailRecipients.push(requirement?.AssignedProcessor) : emailRecipients.push(requirement?.AssignedProcessor?.userID)
                }
            }
            filteredRoles = filteredRoles?.filter(role => role !== TRMRoles?.sales && role !== TRMRoles?.processor)
            for (const role of filteredRoles) {
                const groupUsers = await getAllUsersFromGroupName(role) as any[]
                groupUsers?.forEach((person: any) => {
                    if (!emailRecipients?.includes(person.rid) && isEmailEnabled()) {
                        emailRecipients?.push(person.rid)
                    }
                })
            }
        } else {
            if (filteredRoles === TRMRoles?.sales && requirement?.AnewAccOwnerID) {
                const AnewAccOwnerID = typeof (requirement?.AnewAccOwnerID) === 'string' ? requirement?.AnewAccOwnerID : requirement?.AnewAccOwnerID?.userID
                emailRecipients.push(AnewAccOwnerID)
                if (ACTIVITY_ID?.WorkOrderCanceled === activityID || ACTIVITY_ID?.InquiryCanceled === activityID) {
                    const getUserId = cancelEmail ? ([...await anewWhoHttp().Picker(cancelEmail)])[0].rid : null;
                    if(getUserId && isEmailEnabled()){
                        emailRecipients.push(getUserId)
                    }
                }
            } else if (filteredRoles === TRMRoles?.processor && requirement?.AssignedProcessor) {
                typeof (requirement?.AssignedProcessor) === 'string' ? emailRecipients.push(requirement?.AssignedProcessor) : emailRecipients.push(requirement?.AssignedProcessor?.userID)
            } else if(filteredRoles !== '' && filteredRoles !== TRMRoles?.sales && filteredRoles !== TRMRoles?.processor){
                // If filteredRoles is not an array, treat it as a single role
                // Make sure to exclude sales and processor roles which are handled separately
                const groupUsers = await getAllUsersFromGroupName(filteredRoles) as any[]
                groupUsers?.forEach((person: any) => {
                    if (!emailRecipients?.includes(person.rid) && isEmailEnabled()) {
                        emailRecipients?.push(person.rid)
                    }
                })
            }
        }
    }

    if (specificUserNotifications?.includes(notification.ActivityID)) {
        if (extraData?.teamMemberAddedOrRemoved && typeof (extraData?.teamMemberAddedOrRemoved) !== 'string') {
            extraData?.teamMemberAddedOrRemoved?.forEach((item: any) => {
                // For this specific instance do NOT use the rid. We are passing in the userId via extraData
                emailRecipients?.push(item?.userId)
            })
        }
        if(requirement?.userId && typeof (requirement?.userId) === 'string' && userRoles === customerRoles[0]){
            emailRecipients?.push(requirement?.userId)
        }
    }
    await addRecipientsFromGroup()

    return emailRecipients
}
const ccEmailTo = async (
    requirement: I_InquiryList,
    notification: any,
    sendTo: string[],
    activityID: any
): Promise<string[]> => {
    const emailRecipients: string[] = []

    // Temporary hard coded value for sending all emails
    // This will change with User Prefs later in settings
    const isEmailEnabled = () => !USER_DISABLED_EMAILS

    const userRoles = notification?.CCUsers
    let filteredRoles = userRoles
    if (typeof userRoles === 'string') {
        if (userRoles?.includes(',')) {
            filteredRoles = userRoles?.split(',')?.map(role => findRolesFromNotifications(requirement, role))
        } else {
            filteredRoles = findRolesFromNotifications(requirement, notification?.ToUserRoles)
        }
    }
    if (userRoles) {
        const addRecipientsFromUser = async (userId: string) => {
            const user = await anewWhoHttp().OtherByUserId(userId)
            if (user?.rid && isEmailEnabled()) {
                emailRecipients.push(user.rid)
            }
        }

        const addRecipientsFromGroup = async () => {
            const groupUsers = await getAllUsersFromGroupName(filteredRoles) as any[]
            groupUsers?.forEach((person: any) => {
                // Add the user's email to emailRecipients if it's not already present
                if (!emailRecipients?.includes(person.rid) && isEmailEnabled()) {
                    emailRecipients?.push(person.rid)
                }
            })
        }
        await addRecipientsFromGroup()
        const ccTo = emailRecipients?.filter((email) => !sendTo?.includes(email))

        return ccTo
    }
    return ['']
}
const buildEmailSubject = async (
    requirement: I_InquiryList|any,
    notification: any,
    extraData?: { fileName?: string },
    currentUser?: Me | null
): Promise<string> => {
    if (requirement && notification?.Subject) {
        let subjectWithReplacedValues = notification?.Subject

        // Replace [RequirementID] with the requirement's GUID
        subjectWithReplacedValues = subjectWithReplacedValues?.replace(
            /\[ID#\]/g,
            String(requirement?.ID).padStart(6, '0')
        )

        if (subjectWithReplacedValues?.includes('[UserName]')) {
            if (currentUser) {
                const completedBy = await anewWhoHttp().OtherByUserId(currentUser.userId)
                if (completedBy?.persona) {
                    subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                        /\[UserName\]/g,
                        completedBy?.persona
                    )
                }
            }
        }

        if (subjectWithReplacedValues?.includes('[SubmitterName]')) {
            const submitter = await anewWhoHttp().OtherByUserId(requirement.ItemCreatedBy?.userID ?? '')
            if (submitter?.persona) {
                subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                    /\[SubmitterName\]/g,
                    submitter.persona
                )
            }
        }
        if (subjectWithReplacedValues?.includes('[ProcessorName]')) {
            if (requirement?.AssignedProcessor) {
                const processorId = typeof (requirement?.AssignedProcessor) === 'string' ? requirement?.AssignedProcessor : requirement?.AssignedProcessor?.userID
                const processorName = await anewWhoHttp().OtherByUserId(processorId)
                if (processorName?.persona) {
                    subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                        /\[ProcessorName\]/g,
                        processorName.persona
                    )
                }
            }
        }

        if (extraData?.fileName && subjectWithReplacedValues?.includes('[DocName]')) {
            subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                /\[DocName\]/g,
                extraData.fileName
            )
        }

        if (subjectWithReplacedValues?.includes('[CompletedBy]') || subjectWithReplacedValues?.includes('[RedirectedBy]')) {
            if (currentUser) {
                const completedBy = await anewWhoHttp().OtherByUserId(currentUser?.userId ?? '')
                if (completedBy?.persona) {
                    subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                        /\[CompletedBy\]|\[RedirectedBy\]/g,
                        completedBy.persona
                    )
                }
            }
        }
        if (subjectWithReplacedValues?.includes('[addedbyUSERNAME]')) {
            if (currentUser) {
                const addedByUser = await anewWhoHttp().OtherByUserId(currentUser.userId ?? '')
                if (addedByUser?.persona) {
                    subjectWithReplacedValues = subjectWithReplacedValues.replace(
                        /\[addedbyUSERNAME]/g,
                        addedByUser.persona
                    )
                }
            }
        }
        if (requirement?.defaultDisplay && subjectWithReplacedValues?.includes('[First Name Last Name]')) {
            subjectWithReplacedValues = subjectWithReplacedValues?.replace(
                /\[First Name Last Name\]/g,
                requirement?.defaultDisplay
            )
        }
        // Add more replacement logic as needed

        return subjectWithReplacedValues
    }

    // If no matching item or no Subject field is found, use a default subject
    return ''
}
const buildEmailBody = async (
    requirement: any,
    notification: any,
    userContext: Me | null,
    extraData?: { commentSubject?: string, comment?: string, parentCommentID?: string },
    activityID?: string,
    vehicleInformation?:any
) => {
    const baseURL = window.location.origin
    let body = ''
    let bodyWithReplacedValues = notification.Body
        // Replace [Here] with a link to the requirement
        if (bodyWithReplacedValues?.toLowerCase()?.includes('click here')) {
            bodyWithReplacedValues = bodyWithReplacedValues?.replace(
                /click here/i,
                `<a href="${getRequirementURL(requirement)}">Click here</a>`
            );
        }

        // Replace [RequirementID] with the requirement ID
        if (bodyWithReplacedValues?.includes('[ID#]')) {
            bodyWithReplacedValues = bodyWithReplacedValues?.replace(
                /\[ID#\]/g,
                String(requirement?.ID).padStart(6, '0')
            )
        }
        if (requirement?.defaultDisplay && requirement?.email && bodyWithReplacedValues?.includes('[First Name Last Name, email address]')) {
            bodyWithReplacedValues = bodyWithReplacedValues?.replace(
                /\[First Name Last Name, email address\]/g,
                requirement?.defaultDisplay + ', ' + requirement?.email
            )
        }
        if (bodyWithReplacedValues?.includes('[URL]')) {
            bodyWithReplacedValues = bodyWithReplacedValues?.replace(
                /\[URL\]/g,
                `<a href="${baseURL}">${baseURL}</a>`
            )
        }
        if (bodyWithReplacedValues?.includes('[ProcessorName]')) {
            if (requirement?.AssignedProcessor) {
                const processorId = typeof (requirement?.AssignedProcessor) === 'string' ? requirement?.AssignedProcessor : requirement?.AssignedProcessor?.userID
                const processorName = await anewWhoHttp().OtherByUserId(processorId)
                if (processorName?.persona) {
                    bodyWithReplacedValues = bodyWithReplacedValues.replace(
                        /\[ProcessorName\]/g,
                        processorName.persona
                    )
                }
            }
        }
        if (bodyWithReplacedValues?.includes('[UserName]')) {
            if (userContext?.userId) {
                const completedBy = await anewWhoHttp().OtherByUserId(userContext.userId)
                if (completedBy?.persona) {
                    bodyWithReplacedValues = bodyWithReplacedValues.replace(
                        /\[UserName\]/g,
                        completedBy?.persona
                    )
                }
            }
        }

        // Replace [CommentSubject] with the passed commentSubject
        if (extraData?.commentSubject && bodyWithReplacedValues?.includes('[CommentSubject]')) {
            bodyWithReplacedValues = bodyWithReplacedValues?.replace(
                /\[CommentSubject\]/g,
                extraData.commentSubject
            )
        }

        // Ensure \n is treated as an actual newline
        bodyWithReplacedValues = bodyWithReplacedValues?.replace(/\\n/g, '<br/>');

        if (extraData?.comment) {
            // Replace [Comment] with the passed comment
            bodyWithReplacedValues = bodyWithReplacedValues.replace(
                /\[Comment\]/gi,
                `[Comment]\n${extraData.comment}`
            );

            // Replace "Comments" followed by optional spaces/newlines and insert extraData.comment
            bodyWithReplacedValues = bodyWithReplacedValues.replace(
                /(Comments\s*\n\s*)/i,
                `$1${extraData.comment}\n`
            );
        }
    if (activityID === ACTIVITY_ID?.InquiryCanceled || activityID === ACTIVITY_ID?.WorkOrderCanceled) {
    const rows = `
        <tr>
            <td style="padding:8px; border:1px solid #ddd; width:100px; word-break:break-word;">
                <a href="${getRequirementURL(requirement)}">${String(requirement?.ID || '').padStart(6, '0')}</a>
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-word;">
                ${escapeHTML(requirement?.AccName)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:140px; word-break:break-word;">
                ${!requirement?.SubClientName ? '--' : escapeHTML(requirement.SubClientName)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-word;">
                ${escapeHTML(requirement?.AnewAccountOwner)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-all;">
                ${escapeHTML(requirement?.VINNumber)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px; word-break:break-word;">
                ${escapeHTML(vehicleInformation?.Year)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px; word-break:break-word;">
                ${escapeHTML(vehicleInformation?.Make)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px; word-break:break-word;">
                ${escapeHTML(vehicleInformation?.Model)}
            </td>
        </tr>
    `;

    const body = `
    <!DOCTYPE html>
    <html>
    <body style="margin:0; padding:0; font-family:Segoe UI, Verdana, sans-serif;">

        <!-- Header -->
        <table width="100%" cellpadding="0" cellspacing="0" style="background:#efeff7;">
            <tr>
                <td style="padding:12px 15px;">
                    <h1 style="margin:0; font-size:18px; color:#00529b;">ANEW</h1>
                </td>
            </tr>
        </table>

        <!-- Content -->
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #B7B7B7;">
            <tr>
                <td style="padding:10px;">

                    <table width="100%" cellpadding="0" cellspacing="0"
                        style="border-collapse:collapse; font-size:12px;">
                        <tr>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:100px;">${requirement?.IsWorkOrder ? 'TRM Work Order #' : 'TRM Inquiry #'}</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">Account Name</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:140px;">Sub-Client Name</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">Anew Account Owner</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">VIN</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Year</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Make</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Model</th>
                        </tr>
                        ${rows}
                    </table>
                        <div style="margin-top:15px;padding:15px;border:1px solid #e0e0e0;background:#f9fafb;border-radius:6px;font-family:Segoe UI,Verdana,sans-serif;font-size:13px;color:#333;line-height:1.6;">
                            ${bodyWithReplacedValues}
                        </div>
                </div>
                </td>
            </tr>

            <!-- Footer -->
            <tr>
                <td style="padding:10px; font-size:12px; color:#555; border-top:1px solid #B7B7B7;">
                    PLEASE DO NOT REPLY. This mailbox is unmonitored. For any questions, please contact Anew Team.
                </td>
            </tr>
        </table>

    </body>
    </html>
    `;
    return body;
    } else {
        body += '<!doctype html>'
        body +=
            '<HTML><HEAD> <META name=GENERATOR content="MSHTML 11.00.10570.1001"></HEAD> <BODY>'
        body +=
            '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; FONT-WEIGHT: 600; MARGIN: 0px auto" cellSpacing=0 cellPadding=0 width=620 border=0>'
        body += '<TBODY>'
        body +=
            '<TR><TD style="BORDER-COLLAPSE: collapse; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 15px; MARGIN: 0px; BACKGROUND-COLOR: #efeff7" vAlign=top border="0" cellpadding="0" cellspacing="0">'
        body +=
            '<H1 style="FONT-SIZE: 18px; MARGIN-BOTTOM: 0px; FONT-FAMILY: Arial, Helvetica, sans-serif; MARGIN-TOP: 0px; FONT-WEIGHT: bold; COLOR: #00529b; PADDING-BOTTOM: 0px; PADDING-TOP: 0px">ANEW</H1>'
        // body +=
        //     `<H2 style="FONT-SIZE: 14px; MARGIN-BOTTOM: 0px; FONT-FAMILY: Arial, Helvetica, sans-serif; MARGIN-TOP: 0px; FONT-WEIGHT: bold; PADDING-BOTTOM: 0px; PADDING-TOP: 0px">${processText}</H2>`
        body += '</TD></TR>'
        body += '<TR><TD>'
        body +=
            '<TABLE style="BORDER-RIGHT: #B7B7B7 1px solid; BORDER-LEFT: #B7B7B7 1px solid" cellSpacing=0 cellPadding=0 width=620>'
        body += '<TBODY>'
        body +=
            '<TR><TD style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #efeff7 1px solid; COLOR: #323232; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
        body +=
            '<TABLE style="FONT-SIZE: 13px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'" cellSpacing=0 cellPadding=0 width=620>'
        body += '<TBODY>'
        body +=
            '<TR><TD style="FONT-SIZE: 12px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; COLOR: #333333; PADDING-BOTTOM: 5px; PADDING-TOP: 5px">'
        body += '' + bodyWithReplacedValues + ''
        body += '</TD></TR>'
        body +=
            '<TR> <TD style="FONT-SIZE: 12px; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; COLOR: #333333; PADDING-BOTTOM: 5px; PADDING-TOP: 5px">'
        // body += '' + clickHereText + ''
        body += '</TD></TR>'
        body += '</TBODY>'
        body += '</TABLE>'
        body += '</TD></TR>'
        body +=
            '<TR> <TD style="FONT-SIZE: 12px; BORDER-TOP: #B7B7B7 1px solid; FONT-FAMILY: Segoe, Segoe UI, DejaVu Sans, Trebuchet MS, Verdana,\' sans-serif\'; BORDER-RIGHT: #B7B7B7 1px solid; BORDER-BOTTOM: #B7B7B7 1px solid; COLOR: #555555; PADDING-BOTTOM: 10px; PADDING-TOP: 10px; PADDING-LEFT: 10px; PADDING-RIGHT: 10px">'
        body += 'PLEASE DO NOT REPLY. This mailbox is unmonitored, if you have any questions, please contact Anew Team.'
        body += '</TD></TR></TBODY></TABLE>'
        body += '</TD></TR></TBODY></TABLE></BODY> </HTML>'

        return body
    }
}
const getRequirementURL = (requirement: I_InquiryList): string => {
    const baseURL = window.location.origin
    let fullURL
    if(requirement.IsWorkOrder){
        fullURL = `${baseURL}/workorders-detailview/${requirement?.InquiryGUID}`
    } else{
        fullURL = `${baseURL}/inquiries-detailview/${requirement?.InquiryGUID}`
    }
    return fullURL
}
export const sendEmail = async (
    emailObj:any,
    requirement: I_InquiryList,
    notification: any,
    currentUser: Me | null
) => {
    const toEmails = Array.isArray(emailObj.To)
        ? emailObj?.To
        : emailObj.To?.split(',')?.map((email: string) => email?.trim())

    const request = {
        to: toEmails,
        cc: emailObj.CC,
        bcc: [],
        subject: emailObj?.Subject,
        body: emailObj?.Body?.replace(/\n/g, ""),
        isHtml: true
    }

    const notifyID = await anewNotifyHttp().Send(request)
    if (notifyID > 1) {
        addToEmailLogList(emailObj, true, "Success", requirement, notification, currentUser)
    } else {
        addToEmailLogList(emailObj, false, "Error", requirement, notification, currentUser)
    }
}
export const addToEmailLogList = async (
    emailObj: any,
    emailSent: boolean,
    errorInfo: any,
    requirement: I_InquiryList,
    notification: any,
    currentUser: Me | null) => {
    const LIST_NAME = "TRMEmailLog"
    const emailList = await anewRListsHttp().getList(LIST_NAME)
    const EmailLogCols = {
        Action: 'Action',
        Body: 'Body',
        EKey: 'eKey',
        ETag: 'eTag',
        ErrorInformation: 'ErrorInformation',
        From: 'From',
        EmailSent: 'IsMailSent',
        ItemCreated: 'ItemCreated',
        ItemCreatedBy: 'ItemCreatedBy',
        ItemGUID: 'ItemGUID',
        ItemID: 'ItemID',
        ItemModified: 'ItemModified',
        ItemModifiedBy: 'ItemModifiedBy',
        Subject: 'Subject',
        To: 'To',
        Role: 'Role',
        Comment: 'Comment',
        Title: 'Title'
    }

    const addObj: any = {}

    addObj[EmailLogCols.Action] = notification.ActivityID ? notification.ActivityID : ''
    addObj[EmailLogCols.ItemID] = requirement.ID ? String(requirement?.ID).padStart(6, '0') : ''
    addObj[EmailLogCols.ItemGUID] = requirement.InquiryGUID ? requirement.InquiryGUID : ''
    addObj[EmailLogCols.From] = 'no-reply@sharepointonline.com'
    addObj[EmailLogCols.To] = emailObj.To ? emailObj.To.join(', ') : ''
    addObj[EmailLogCols.Subject] = emailObj.Subject
    addObj[EmailLogCols.EmailSent] = emailSent
    addObj[EmailLogCols.ItemCreated] = new Date().toISOString()
    addObj[EmailLogCols.ItemModified] = new Date().toISOString()
    addObj[EmailLogCols.ItemCreatedBy] = currentUser ? currentUser.userId : ''
    addObj[EmailLogCols.ItemModifiedBy] = currentUser ? currentUser.userId : ''
    addObj[EmailLogCols.Body] = emailObj.Body
    addObj[EmailLogCols.Title] = ''
    addObj[EmailLogCols.ErrorInformation] = errorInfo === "Error" ? "error" : ""
    try {
        await emailList.addOrUpdateItem(addObj)
    } catch (error: any) {
        console.error(`Error while adding email to log: ${error}`)
        console.error(error.name)
    }
}
export const sendWorkOrderConversionMail = async (
    requirement: I_InquiryList[] | null,
    activityID: string,
    currentUser: Me | null,
    dlEmail: string
) => {
    try {
    const getUserId = ([...await anewWhoHttp().Picker(dlEmail)])[0].rid;
    const subject = `Status change to ${requirement?.[0]?.StatusTitle} on ${convertDate(new Date(), 'date')}`;
    const rows = (requirement ?? []).map(item => `
        <tr>
            <td style="padding:8px; border:1px solid #ddd; width:100px; word-break:break-word;">
                <a href="#">${String(item?.ID || '').padStart(6, '0')}</a>
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-all;">
                ${escapeHTML(item.VINNumber)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-word;">
                ${escapeHTML(item.AccName)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:140px; word-break:break-word;">
                ${escapeHTML(item.SubClientName) === null || escapeHTML(item.SubClientName)===''? '--' :escapeHTML(item.SubClientName)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px;">
                ${AddressBuilder(item,'Registration') !== ''? AddressBuilder(item,'Registration') : '--'}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px;">
                ${AddressBuilder(item,'Title') !== ''? AddressBuilder(item,'Title') : '--'}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px;">
                ${convertDate(item.WorkOrderCreated, 'date')}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:150px; word-break:break-word;">
                ${escapeHTML(item.AnewAccountOwner)}
            </td>

            <td style="padding:8px; border:1px solid #ddd; width:120px; word-break:break-word;">
                ${escapeHTML(item.ProcessType)}
            </td>
        </tr>
    `).join('');

    const body = `
    <!DOCTYPE html>
    <html>
    <body style="margin:0; padding:0; font-family:Segoe UI, Verdana, sans-serif;">

        <!-- Header -->
        <table width="100%" cellpadding="0" cellspacing="0" style="background:#efeff7;">
            <tr>
                <td style="padding:12px 15px;">
                    <h1 style="margin:0; font-size:18px; color:#00529b;">ANEW</h1>
                </td>
            </tr>
        </table>

        <!-- Content -->
        <table width="100%" cellpadding="0" cellspacing="0" style="border:1px solid #B7B7B7;">
            <tr>
                <td style="padding:10px;">

                    <table width="100%" cellpadding="0" cellspacing="0"
                        style="border-collapse:collapse; font-size:12px;">

                        <tr>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:100px;">TRM Work Order #</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">VIN</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">Account Name</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:140px;">Sub-Client</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Registration To Address</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Title To Address</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Work Order Created</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:150px;">Anew Account Owner</th>
                            <th style="padding:8px; border:1px solid #ddd; background:#e7eaec; width:120px;">Process Type</th>
                        </tr>

                        ${rows}

                    </table>

                </td>
            </tr>

            <!-- Footer -->
            <tr>
                <td style="padding:10px; font-size:12px; color:#555; border-top:1px solid #B7B7B7;">
                    PLEASE DO NOT REPLY. This mailbox is unmonitored. For any questions, please contact Anew Team.
                </td>
            </tr>
        </table>

    </body>
    </html>
    `;

    const request = {
        to: [getUserId],
        cc: [],
        bcc: [],
        subject,
        body,
        isHtml: true
    };

    const notifyID = await anewNotifyHttp().Send(request);

    if (notifyID > 1) {
        addConvertionEmailLogList(request, true, "Success", requirement, activityID, currentUser);
    } else {
        addConvertionEmailLogList(request, false, "Error", requirement, activityID, currentUser);
    }
} catch (error) {
    console.error('Error sending work order conversion email:', error)
}
}

 const addConvertionEmailLogList = async(
    emailObj: any,
    emailSent: boolean,
    errorInfo: any,
    requirements: any,
    ActivityID:string,
    currentUser: Me | null) => {
    const LIST_NAME = "TRMEmailLog"
    const emailList = await anewRListsHttp().getList(LIST_NAME)
    const EmailLogCols = {
        Action: 'Action',
        Body: 'Body',
        EKey: 'eKey',
        ETag: 'eTag',
        ErrorInformation: 'ErrorInformation',
        From: 'From',
        EmailSent: 'IsMailSent',
        ItemCreated: 'ItemCreated',
        ItemCreatedBy: 'ItemCreatedBy',
        ItemGUID: 'ItemGUID',
        ItemID: 'ItemID',
        ItemModified: 'ItemModified',
        ItemModifiedBy: 'ItemModifiedBy',
        Subject: 'Subject',
        To: 'To',
        Role: 'Role',
        Comment: 'Comment',
        Title: 'Title'
    }

    const addObj: any = {}

    addObj[EmailLogCols.Action] = ActivityID ? ActivityID : ''
    addObj[EmailLogCols.ItemID] = ''
    addObj[EmailLogCols.ItemGUID] = emailObj.subject 
    addObj[EmailLogCols.From] = 'no-reply@sharepointonline.com'
    addObj[EmailLogCols.To] = emailObj.To ? emailObj.To.join(', ') : ''
    addObj[EmailLogCols.Subject] = emailObj.subject
    addObj[EmailLogCols.EmailSent] = emailSent
    addObj[EmailLogCols.ItemCreated] = new Date().toISOString()
    addObj[EmailLogCols.ItemModified] = new Date().toISOString()
    addObj[EmailLogCols.ItemCreatedBy] = currentUser ? currentUser.userId : ''
    addObj[EmailLogCols.ItemModifiedBy] = currentUser ? currentUser.userId : ''
    addObj[EmailLogCols.Body] = emailObj.body
    addObj[EmailLogCols.Title] = ''
    addObj[EmailLogCols.ErrorInformation] = errorInfo === "Error" ? "error" : ""
    try {
      await emailList.addOrUpdateItem(addObj)
    } catch (error: any) {
        console.error(`Error while adding email to log: ${error}`)
        console.error(error.name)
    }
}

const AddressBuilder = (requirement: I_InquiryList,addressType: string): string => {
    const addressParts = addressType=== 'Registration' ? [
        requirement?.RegStreetname ? requirement.RegStreetname : '',
        requirement?.RegUnit ? requirement.RegUnit : '',
        requirement?.RegCity ? requirement.RegCity : '',
        requirement?.RegCounty ? requirement.RegCounty : '',
        requirement?.RegState ? requirement.RegState : '',
        requirement?.RegZipCode ? requirement.RegZipCode : ''
    ].filter(part => part !== ''):[
        requirement?.TitlestreetName ? requirement.TitlestreetName : '',
        requirement?.TitleUnit ? requirement.TitleUnit : '',
        requirement?.TitleCity ? requirement.TitleCity : '',
        requirement?.TitleCounty ? requirement.TitleCounty : '',
        requirement?.TitleState ? requirement.TitleState : '',
        requirement?.TitleZipCode ? requirement.TitleZipCode : ''
    ].filter(part => part !== '');

    return addressParts.length>0 ? addressParts.join(', ') : '';
}   
